import { c as _c } from "react/compiler-runtime";
import { format, parseISO, startOfYesterday } from 'shared/dateFns';
import Grid from '@precis-digital/kurama/Grid';
import { type DateRange as V2DateRange } from '@precis-digital/kurama/DatePickerV2';
import { useTheme } from '@precis-digital/kurama/utils';
import { useRef } from 'react';
import { type TFunction } from 'i18next';
import { type UseFormSetValue } from 'react-hook-form';
import { type StandardReportResp } from 'shared/api/standardReports';
import Filter, { type ActiveFilter, type FilterFormValues, type Option } from 'shared/components/Filter';
import { type Item, type ItemComponentProps } from 'shared/components/Filter/FilterFlyout';
import { OperatorFilterItem, ParameterFilterItem } from 'shared/components/Filter/FilterItems';
import FilterListItemLabel from 'shared/components/Filter/ListItemLabel';
import { transformFormValuesToFilters, updateFilterFormValues } from 'shared/components/Filter/utils';
import StatusWithBadge from 'shared/components/StatusWithBadge';
import { type ConfigStatus, type ErrorCodeType, type ErrorDetails } from 'shared/configStatuses';
import { OPERATOR_OPTIONS } from 'shared/constants/filterOperators';
import { getDateRangeFromDatePickerV2ComparisonPreset, type DatePickerV2ComparisonPresetValue, useDatePickerV2ComparisonPresets } from 'shared/hooks/useDatePickerV2ComparisonPresets';
import { useDatePickerV2Presets } from 'shared/hooks/useDatePickerV2Presets';
import { useTranslation } from 'shared/translations';
import MoreMenuDropdown, { type MoreMenuDropdownProps } from './MoreMenuDropDown';
import { checkIfReportTemplateWithNoConversionEvents, type HomePageFilterParameter, type TimePeriodType } from './utils';
interface ToolbarProps {
  selectedStandardReportConfig: StandardReportResp | undefined;
  statusInfo: {
    selectedReportStatus: ConfigStatus | undefined;
    selectedReportErrorCode?: ErrorCodeType | null;
    selectedReportErrorDetails?: ErrorDetails | null;
    selectedReportStatusLastUpdated?: string | null;
    selectedReportStatusMessage?: string | null;
  };
  filterProps: {
    canFilter: boolean;
    selectedFilters: Array<ActiveFilter<HomePageFilterParameter>>;
    setSelectedFilters: React.Dispatch<React.SetStateAction<Array<ActiveFilter<HomePageFilterParameter>>>>;
    selectedDate: {
      startDate: string;
      endDate: string;
    };
    changeDateRange: (startDate: string | null, endDate: string | null) => void;
    changeComparisonRange: (startDate: string, endDate: string) => void;
    customComparisonRange: {
      startDate: string;
      endDate: string;
    } | null;
    handleApplyDateRangeClick: () => void;
    setUserSelectedPeriodType: (periodType: TimePeriodType) => void;
    parameterOptions: Option[];
    additionalCustomControls?: React.ReactNode;
    userSelectedPeriodType: TimePeriodType;
    dateRangeCalendarRef?: React.RefObject<HTMLButtonElement | null>;
    filterBarRef?: React.RefObject<HTMLDivElement | null>;
  };
  moreMenuProps: MoreMenuDropdownProps;
}
const createFilterItems = (parameterOptions: Option[], t: TFunction<'home'>, tCommon: TFunction): Array<Item<FilterFormValues>> => [{
  title: t('filter.selectDimension'),
  key: 'parameter',
  Component: (props: ItemComponentProps<FilterFormValues>) => <ParameterFilterItem parameterOptions={parameterOptions} {...props} />
}, {
  title: t('filter.createFilterConditions'),
  key: 'operator',
  disabledAccordion: true,
  Component: (props: ItemComponentProps<FilterFormValues>) => <OperatorFilterItem t={t} tCommon={tCommon} {...props} />
}];
const Toolbar = t0 => {
  const $ = _c(74);
  const {
    statusInfo,
    selectedStandardReportConfig,
    filterProps: t1,
    moreMenuProps: t2
  } = t0;
  const {
    canFilter,
    selectedFilters,
    setSelectedFilters,
    selectedDate,
    changeDateRange,
    changeComparisonRange,
    customComparisonRange,
    handleApplyDateRangeClick,
    setUserSelectedPeriodType,
    userSelectedPeriodType,
    parameterOptions,
    additionalCustomControls,
    dateRangeCalendarRef,
    filterBarRef
  } = t1;
  const {
    canSwitchReports,
    disableConversionEvents,
    handleSwitchReports,
    handleSelectTopCardMetrics,
    handleSelectConversionEvents,
    topCardMetricsCount,
    conversionEventsCount,
    handleSelectBudgetAndTargets,
    moreMenuButtonRef
  } = t2;
  const theme = useTheme();
  const {
    t
  } = useTranslation("home");
  const internalDateRangeCalendarRef = useRef(null);
  const resolvedDateRangeCalendarRef = dateRangeCalendarRef ?? internalDateRangeCalendarRef;
  const {
    t: tCommon
  } = useTranslation("common");
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = startOfYesterday();
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const maxDate = t3;
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      maxDate
    };
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const presets = useDatePickerV2Presets(t4);
  const comparisonPresets = useDatePickerV2ComparisonPresets();
  let v2ComparisonPreset;
  let v2ComparisonRange;
  let v2SelectedRange;
  if ($[2] !== customComparisonRange || $[3] !== selectedDate.endDate || $[4] !== selectedDate.startDate || $[5] !== userSelectedPeriodType) {
    v2SelectedRange = selectedDate.startDate !== "" && selectedDate.endDate !== "" ? {
      from: parseISO(selectedDate.startDate),
      to: parseISO(selectedDate.endDate)
    } : null;
    v2ComparisonPreset = "previousPeriod";
    if (userSelectedPeriodType === "previous_year") {
      v2ComparisonPreset = "previousYearsPeriod";
    } else {
      if (userSelectedPeriodType === "custom") {
        v2ComparisonPreset = "custom";
      }
    }
    v2ComparisonRange = null;
    if (v2SelectedRange != null) {
      if (v2ComparisonPreset === "custom") {
        v2ComparisonRange = customComparisonRange == null ? getDateRangeFromDatePickerV2ComparisonPreset(v2SelectedRange, "previousPeriod") : {
          from: parseISO(customComparisonRange.startDate),
          to: parseISO(customComparisonRange.endDate)
        };
      } else {
        v2ComparisonRange = getDateRangeFromDatePickerV2ComparisonPreset(v2SelectedRange, v2ComparisonPreset);
      }
    }
    $[2] = customComparisonRange;
    $[3] = selectedDate.endDate;
    $[4] = selectedDate.startDate;
    $[5] = userSelectedPeriodType;
    $[6] = v2ComparisonPreset;
    $[7] = v2ComparisonRange;
    $[8] = v2SelectedRange;
  } else {
    v2ComparisonPreset = $[6];
    v2ComparisonRange = $[7];
    v2SelectedRange = $[8];
  }
  let t5;
  if ($[9] !== selectedStandardReportConfig) {
    t5 = checkIfReportTemplateWithNoConversionEvents(selectedStandardReportConfig);
    $[9] = selectedStandardReportConfig;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const isReportTemplateWithNoConversionEvents = t5;
  let t6;
  if ($[11] !== parameterOptions || $[12] !== t || $[13] !== tCommon) {
    t6 = createFilterItems(parameterOptions, t, tCommon);
    $[11] = parameterOptions;
    $[12] = t;
    $[13] = tCommon;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const filterItems = t6;
  let t7;
  if ($[15] !== theme) {
    t7 = theme.spacing(1);
    $[15] = theme;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== theme) {
    t8 = theme.spacing(1);
    $[17] = theme;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== selectedStandardReportConfig?.configId || $[20] !== selectedStandardReportConfig?.name || $[21] !== selectedStandardReportConfig?.runSchedule || $[22] !== statusInfo.selectedReportErrorCode || $[23] !== statusInfo.selectedReportErrorDetails || $[24] !== statusInfo.selectedReportStatus || $[25] !== statusInfo.selectedReportStatusLastUpdated) {
    t9 = statusInfo.selectedReportStatus != null && <StatusWithBadge status={statusInfo.selectedReportStatus} typographyVariant="h5" lastUpdated={statusInfo.selectedReportStatusLastUpdated ?? ""} errorCode={statusInfo.selectedReportErrorCode} errorDetails={statusInfo.selectedReportErrorDetails} dagViewInformation={{
      dagViewType: "reportingSolution",
      configId: selectedStandardReportConfig?.configId ?? 0,
      configName: selectedStandardReportConfig?.name ?? "",
      isActive: selectedStandardReportConfig?.runSchedule ?? false,
      isPageWithConfigId: true
    }} />;
    $[19] = selectedStandardReportConfig?.configId;
    $[20] = selectedStandardReportConfig?.name;
    $[21] = selectedStandardReportConfig?.runSchedule;
    $[22] = statusInfo.selectedReportErrorCode;
    $[23] = statusInfo.selectedReportErrorDetails;
    $[24] = statusInfo.selectedReportStatus;
    $[25] = statusInfo.selectedReportStatusLastUpdated;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== additionalCustomControls || $[28] !== canFilter || $[29] !== changeComparisonRange || $[30] !== changeDateRange || $[31] !== comparisonPresets || $[32] !== filterBarRef || $[33] !== filterItems || $[34] !== handleApplyDateRangeClick || $[35] !== parameterOptions || $[36] !== presets || $[37] !== resolvedDateRangeCalendarRef || $[38] !== selectedDate.endDate || $[39] !== selectedDate.startDate || $[40] !== selectedFilters || $[41] !== setSelectedFilters || $[42] !== setUserSelectedPeriodType || $[43] !== t || $[44] !== tCommon || $[45] !== v2ComparisonPreset || $[46] !== v2ComparisonRange || $[47] !== v2SelectedRange) {
    t10 = canFilter && <Filter key={`${selectedDate.startDate}-${selectedDate.endDate}`} updateFormValues={updateFilterFormValues({
      parameterOptions
    }) as (filter: ActiveFilter<HomePageFilterParameter>, setValue: UseFormSetValue<FilterFormValues>) => void} transformFormValuesToFilters={transformFormValuesToFilters} filterItems={filterItems} t={t} activeFilters={selectedFilters} onUpdateFilters={setSelectedFilters} datePickerV2TriggerProps={{
      mode: "range-comparison",
      maxDate,
      selectedRange: v2SelectedRange,
      comparisonRange: v2ComparisonRange,
      onSelectRange: range => {
        if (range?.to != null) {
          changeDateRange(format(range.from, "yyyy-MM-dd"), format(range.to, "yyyy-MM-dd"));
        }
      },
      onSelectComparisonRange: range_0 => {
        if (range_0?.to != null) {
          changeComparisonRange(format(range_0.from, "yyyy-MM-dd"), format(range_0.to, "yyyy-MM-dd"));
        }
      },
      onApplyDateRangeClick: handleApplyDateRangeClick,
      defaultComparisonEnabled: true,
      lockComparisonEnabled: true,
      comparisonPresets,
      selectedComparisonPreset: v2ComparisonPreset,
      onComparisonPresetChange: presetValue => {
        if (presetValue === "previousYearsPeriod") {
          setUserSelectedPeriodType("previous_year");
        } else {
          if (presetValue === "previousPeriod") {
            setUserSelectedPeriodType("previous_period");
          } else {
            if (presetValue === "custom") {
              setUserSelectedPeriodType("custom");
            }
          }
        }
      },
      presets,
      applyText: t("buttons.apply"),
      cancelText: tCommon("buttons.cancel"),
      ref: resolvedDateRangeCalendarRef
    }} parseFilterToChipLabel={_temp2} renderListItemLabel={filter_0 => <FilterListItemLabel filter={filter_0} includeText={t("filter.include")} />} additionalCustomControls={additionalCustomControls} filterBarRef={filterBarRef} />;
    $[27] = additionalCustomControls;
    $[28] = canFilter;
    $[29] = changeComparisonRange;
    $[30] = changeDateRange;
    $[31] = comparisonPresets;
    $[32] = filterBarRef;
    $[33] = filterItems;
    $[34] = handleApplyDateRangeClick;
    $[35] = parameterOptions;
    $[36] = presets;
    $[37] = resolvedDateRangeCalendarRef;
    $[38] = selectedDate.endDate;
    $[39] = selectedDate.startDate;
    $[40] = selectedFilters;
    $[41] = setSelectedFilters;
    $[42] = setUserSelectedPeriodType;
    $[43] = t;
    $[44] = tCommon;
    $[45] = v2ComparisonPreset;
    $[46] = v2ComparisonRange;
    $[47] = v2SelectedRange;
    $[48] = t10;
  } else {
    t10 = $[48];
  }
  let t11;
  if ($[49] !== t10 || $[50] !== t8 || $[51] !== t9) {
    t11 = <Grid xs={true} display="flex" flexDirection="row" gap={t8} alignItems="center" minWidth={0}>{t9}{t10}</Grid>;
    $[49] = t10;
    $[50] = t8;
    $[51] = t9;
    $[52] = t11;
  } else {
    t11 = $[52];
  }
  const t12 = disableConversionEvents || isReportTemplateWithNoConversionEvents;
  let t13;
  if ($[53] !== handleSelectConversionEvents || $[54] !== isReportTemplateWithNoConversionEvents) {
    t13 = isReportTemplateWithNoConversionEvents ? undefined : () => {
      handleSelectConversionEvents?.();
    };
    $[53] = handleSelectConversionEvents;
    $[54] = isReportTemplateWithNoConversionEvents;
    $[55] = t13;
  } else {
    t13 = $[55];
  }
  let t14;
  if ($[56] !== isReportTemplateWithNoConversionEvents || $[57] !== t) {
    t14 = isReportTemplateWithNoConversionEvents && {
      disableConversionEventsReason: t("settingOnlyAvailableForReportType")
    };
    $[56] = isReportTemplateWithNoConversionEvents;
    $[57] = t;
    $[58] = t14;
  } else {
    t14 = $[58];
  }
  let t15;
  if ($[59] !== canSwitchReports || $[60] !== conversionEventsCount || $[61] !== handleSelectBudgetAndTargets || $[62] !== handleSelectTopCardMetrics || $[63] !== handleSwitchReports || $[64] !== moreMenuButtonRef || $[65] !== t12 || $[66] !== t13 || $[67] !== t14 || $[68] !== topCardMetricsCount) {
    t15 = <Grid><MoreMenuDropdown canSwitchReports={canSwitchReports} handleSwitchReports={handleSwitchReports} handleSelectTopCardMetrics={handleSelectTopCardMetrics} disableConversionEvents={t12} handleSelectConversionEvents={t13} topCardMetricsCount={topCardMetricsCount} conversionEventsCount={conversionEventsCount} moreMenuButtonRef={moreMenuButtonRef} {...t14} handleSelectBudgetAndTargets={handleSelectBudgetAndTargets} /></Grid>;
    $[59] = canSwitchReports;
    $[60] = conversionEventsCount;
    $[61] = handleSelectBudgetAndTargets;
    $[62] = handleSelectTopCardMetrics;
    $[63] = handleSwitchReports;
    $[64] = moreMenuButtonRef;
    $[65] = t12;
    $[66] = t13;
    $[67] = t14;
    $[68] = topCardMetricsCount;
    $[69] = t15;
  } else {
    t15 = $[69];
  }
  let t16;
  if ($[70] !== t11 || $[71] !== t15 || $[72] !== t7) {
    t16 = <Grid container={true} gap={t7} direction="row" alignItems="center">{t11}{t15}</Grid>;
    $[70] = t11;
    $[71] = t15;
    $[72] = t7;
    $[73] = t16;
  } else {
    t16 = $[73];
  }
  return t16;
};
export default Toolbar;
function _temp(item) {
  return item.label ?? item.value;
}
function _temp2(filter) {
  const symbol = OPERATOR_OPTIONS[filter.operator].label?.toLowerCase();
  if (filter.data == null) {
    return `${filter.parameter.label} ${symbol}`;
  }
  let value;
  if (Array.isArray(filter.data)) {
    value = filter.data.map(_temp).join(", ");
  } else {
    value = typeof filter.data === "string" ? filter.data : filter.data.label;
  }
  return `${filter.parameter.label} ${symbol} ${value?.toString() ?? ""}`;
}